import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CalendarCheck, MessageCircle, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { api } from "../lib/api";
import { whatsappLink } from "../data/site";
import { useLang } from "../context/LanguageContext";

const fmt = (d) => (d ? format(d, "yyyy-MM-dd") : "");

export function BookingDialog({ trigger, motoName = "", motoId = "" }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState(undefined); // { from, to }
  const [blocked, setBlocked] = useState([]);     // array of Date
  const [showCal, setShowCal] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", start_date: "", end_date: "", message: "",
  });

  // Load fully-booked dates for this moto whenever the dialog opens.
  useEffect(() => {
    if (!open || !motoId) return;
    api.get(`/motorcycles/${motoId}/blocked-dates`)
      .then((r) => setBlocked((r.data.blocked_dates || []).map((s) => new Date(`${s}T00:00:00`))))
      .catch(() => setBlocked([]));
  }, [open, motoId]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onRangeSelect = (r) => {
    setRange(r);
    setForm((f) => ({ ...f, start_date: fmt(r?.from), end_date: fmt(r?.to || r?.from) }));
    if (r?.from && r?.to) setShowCal(false); // collapse once a full range is chosen
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const disabledDays = [{ before: today }, ...blocked];

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error(t.booking.fill);
      return;
    }
    // Build the WhatsApp message with all captured details
    let msg = t.booking.waText(motoName || t.booking.ride, form.start_date, form.end_date, form.name);
    const extras = [];
    if (form.phone) extras.push(`${t.booking.phone}: ${form.phone}`);
    if (form.email) extras.push(`${t.booking.email}: ${form.email}`);
    if (form.message) extras.push(`${t.booking.message}: ${form.message}`);
    if (extras.length) msg += "\n" + extras.join("\n");
    const waUrl = whatsappLink(msg);

    // Open WhatsApp synchronously inside the click gesture so it is NOT blocked
    // by popup blockers (calling window.open after an await gets blocked).
    const win = window.open(waUrl, "_blank", "noopener,noreferrer");
    if (!win) window.location.href = waUrl; // fallback if a blocker still intervenes

    // Persist the lead in the background — does not block the WhatsApp handoff.
    setLoading(true);
    api.post("/bookings", { ...form, motorcycle_id: motoId, motorcycle_name: motoName })
      .then(() => toast.success(t.booking.success))
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 409) toast.error(t.booking.fullyBooked);
        else if (code === 422) toast.error(t.booking.invalidDates);
        else toast.error(t.booking.error);
      })
      .finally(() => {
        setLoading(false);
        setOpen(false);
        setRange(undefined);
        setForm({ name: "", phone: "", email: "", start_date: "", end_date: "", message: "" });
      });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setShowCal(false); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-brand-blue flex items-center gap-2">
            <CalendarCheck size={20} /> {t.booking.title} {motoName || t.booking.ride}
          </DialogTitle>
          <DialogDescription>{t.motoPage.subtitle}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="b-name">{t.booking.name} *</Label>
            <Input id="b-name" data-testid="booking-name" value={form.name} onChange={upd("name")} placeholder={t.booking.name} />
          </div>
          <div>
            <Label>{t.booking.dates} *</Label>
            <button
              type="button"
              data-testid="booking-date-trigger"
              onClick={() => setShowCal((v) => !v)}
              className="w-full flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-sm text-left hover:border-brand-blue transition-colors"
            >
              <CalendarDays size={16} className="text-brand-blue shrink-0" />
              {range?.from ? (
                <span className="text-brand-gray">
                  {fmt(range.from)} {range.to ? `→ ${fmt(range.to)}` : ""}
                </span>
              ) : (
                <span className="text-slate-400">{t.booking.selectDates}</span>
              )}
            </button>
            {showCal && (
              <div data-testid="booking-calendar" className="mt-2 border border-slate-200 rounded-lg flex justify-center bg-white">
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={onRangeSelect}
                  disabled={disabledDays}
                  numberOfMonths={1}
                />
              </div>
            )}
            <input type="hidden" data-testid="booking-start" value={form.start_date} readOnly />
            <input type="hidden" data-testid="booking-end" value={form.end_date} readOnly />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="b-phone">{t.booking.phone}</Label>
              <Input id="b-phone" data-testid="booking-phone" value={form.phone} onChange={upd("phone")} placeholder="+1 ..." />
            </div>
            <div>
              <Label htmlFor="b-email">{t.booking.email}</Label>
              <Input id="b-email" type="email" data-testid="booking-email" value={form.email} onChange={upd("email")} placeholder="you@email.com" />
            </div>
          </div>
          <div>
            <Label htmlFor="b-msg">{t.booking.message}</Label>
            <Textarea id="b-msg" data-testid="booking-message" value={form.message} onChange={upd("message")} placeholder={t.booking.messagePlaceholder} rows={2} />
          </div>
          <DialogFooter>
            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white font-heading font-bold py-3 rounded-full transition-colors"
            >
              <MessageCircle size={18} /> {loading ? t.booking.sending : t.booking.submit}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default BookingDialog;
